import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { marketerAPI } from '../lib/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import {
  LayoutDashboard,
  Users,
  Link2,
  LogOut,
  Copy,
  CheckCircle,
  User,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  MessageCircle,
  Target,
  Trophy,
  PartyPopper,
  PlayCircle,
  Camera,
  Upload,
} from 'lucide-react';

const WHATSAPP_GROUP_LINK_DEFAULT = 'https://chat.whatsapp.com/FXdZkRssQLHEa6pQfni3lp';
const MILESTONE_TARGET = 50;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper to extract YouTube video ID
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : null;
};

const MarketerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [trainingVideoUrl, setTrainingVideoUrl] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [whatsAppGroupLink, setWhatsAppGroupLink] = useState(WHATSAPP_GROUP_LINK_DEFAULT);
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const profileImageInputRef = useRef(null);
  const [celebrationShown, setCelebrationShown] = useState(false);
  const [linkCode, setLinkCode] = useState('');
  const [linkingToRecruiter, setLinkingToRecruiter] = useState(false);

  // Confetti celebration function
  const triggerCelebration = useCallback(() => {
    const duration = 4000;
    const end = Date.now() + duration;

    const colors = ['#1E3A8A', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchTrainingVideo();
    fetchWhatsAppGroup();
  }, []);

  // Check for milestone achievement
  useEffect(() => {
    if (dashboardData && !celebrationShown) {
      const recruitsCount = dashboardData.recruits_count || 0;
      const hasSeenCelebration = localStorage.getItem(`milestone_50_${user?.id}`);
      
      if (recruitsCount >= MILESTONE_TARGET && !hasSeenCelebration) {
        setShowCelebration(true);
        setCelebrationShown(true);
        triggerCelebration();
        localStorage.setItem(`milestone_50_${user?.id}`, 'true');
      }
    }
  }, [dashboardData, celebrationShown, user?.id, triggerCelebration]);

  const fetchDashboard = async () => {
    try {
      const response = await marketerAPI.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingVideo = async () => {
    try {
      // Prefer the new library. Fall back to the legacy single-video setting for backward compat.
      const libRes = await marketerAPI.getTrainings();
      const list = libRes.data?.trainings || [];
      setTrainings(list);
      if (list.length > 0) {
        setTrainingVideoUrl(list[0].video_url);
        return;
      }
      const response = await marketerAPI.getTrainingVideo();
      if (response.data.youtube_url) setTrainingVideoUrl(response.data.youtube_url);
    } catch (error) {
      console.error('fetchTrainings failed (optional):', error);
    }
  };

  const fetchWhatsAppGroup = async () => {
    try {
      const response = await marketerAPI.getWhatsAppGroup();
      if (response.data.whatsapp_group_link) {
        setWhatsAppGroupLink(response.data.whatsapp_group_link);
      }
    } catch (error) {
      console.error('fetchWhatsAppGroup failed (optional):', error);
    }
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const response = await marketerAPI.uploadProfileImage(file);
      // Update the dashboard data with new image URL
      setDashboardData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          profile_image_url: response.data.url
        }
      }));
      toast.success('Profile photo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload profile photo');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCopyLink = async () => {
    const fullLink = `${window.location.origin}${dashboardData?.referral_link}`;
    try {
      await navigator.clipboard.writeText(fullLink);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = fullLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
    }
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLinkToRecruiter = async () => {
    if (!linkCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setLinkingToRecruiter(true);
    try {
      const response = await marketerAPI.linkToRecruiter(linkCode.trim());
      toast.success(response.data.message);
      // Refresh dashboard to show updated data
      fetchDashboard();
      setLinkCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link to recruiter. Please try again.');
    } finally {
      setLinkingToRecruiter(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const { profile, referral_link, recruits_count, recruits } = dashboardData || {};

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-background" data-testid="marketer-dashboard">
      {/* Celebration Modal */}
      {showCelebration && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl transform animate-bounce-in">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Trophy className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-primary-900 mb-3">
              🎉 Congratulations! 🎉
            </h2>
            <p className="text-xl text-primary-700 font-semibold mb-2">
              You've reached 50 recruits!
            </p>
            <p className="text-neutral-600 mb-6">
              Amazing work! You've hit your recruitment milestone. Keep up the great hustle and remember - 
              <span className="font-semibold text-green-600"> once one of your recruits makes a sale, you earn your ₦15,000 bonus!</span>
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              <PartyPopper className="h-6 w-6 text-yellow-500" />
              <span className="text-lg font-medium text-primary-800">You're a Medichi Star!</span>
              <PartyPopper className="h-6 w-6 text-yellow-500" />
            </div>
            <Button
              onClick={() => setShowCelebration(false)}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-primary-600 to-primary-800 hover:from-primary-700 hover:to-primary-900 text-white font-semibold text-lg shadow-lg"
              data-testid="close-celebration-btn"
            >
              Keep Going! 💪
            </Button>
          </div>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-50 w-64 h-full flex flex-col sidebar-dark transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-primary-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary-700 flex items-center justify-center">
                <span className="text-white font-serif text-xl font-semibold">M</span>
              </div>
              <span className="font-serif text-xl text-white">Medichi Tribe</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 rounded-lg text-primary-200 hover:bg-primary-800 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            <button
              onClick={() => setSidebarOpen(false)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary-800 text-white"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="h-5 w-5" />
              Dashboard
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-primary-800">
          <div className="px-4 py-3 mb-3">
            <p className="text-primary-300 text-sm">Logged in as</p>
            <p className="text-white font-medium truncate">{profile?.full_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-neutral-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-neutral-100"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="font-heading text-2xl font-semibold text-primary-900">Dashboard</h1>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-8">
          {/* WhatsApp Group CTA — always at the top so realtors see it first */}
          <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 shadow-card" data-testid="whatsapp-group-card-top">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-primary-900">Join Our Medichi Tribe WhatsApp Group</h3>
                    <p className="text-neutral-text-muted text-sm">Connect with other marketers, get updates, and grow together!</p>
                  </div>
                </div>
                <a href={whatsAppGroupLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto h-12 px-8 rounded-xl bg-green-500 hover:bg-green-600 text-white font-medium text-base shadow-lg shadow-green-500/30 transition-all hover:scale-105" data-testid="join-whatsapp-btn">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Join WhatsApp Group
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Link to Recruiter Card - Only show if marketer has no upline */}
          {profile && !profile.upline_id && (
            <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-card" data-testid="link-recruiter-card">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                      <Link2 className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-primary-900">🔗 Link to Your Recruiter</h3>
                      <p className="text-orange-700 font-medium mt-1">
                        Did someone refer you? Enter their referral code to link yourself to them.
                      </p>
                      <p className="text-sm text-neutral-text-muted mt-1">
                        This helps your recruiter track their team
                      </p>
                    </div>
                  </div>
                  <div className="w-full md:w-auto">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter referral code"
                        value={linkCode}
                        onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                        className="h-11 rounded-xl w-40 font-mono uppercase"
                        data-testid="link-code-input"
                      />
                      <Button
                        onClick={handleLinkToRecruiter}
                        disabled={linkingToRecruiter}
                        className="h-11 px-6 rounded-xl bg-orange-500 hover:bg-orange-600"
                        data-testid="link-recruiter-btn"
                      >
                        {linkingToRecruiter ? 'Linking...' : 'Link'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recruitment Target Banner */}
          {(recruits_count || 0) >= MILESTONE_TARGET ? (
            // Milestone Achieved Card
            <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 shadow-card overflow-hidden" data-testid="target-card">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Trophy className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-primary-900">🏆 Milestone Achieved!</h3>
                      <p className="text-green-700 font-medium mt-1">
                        You've recruited <span className="text-green-600 font-bold">{recruits_count} realtors</span>! 
                        <span className="text-green-600 font-bold"> Now just one sale from your recruits = ₦15,000 bonus!</span>
                      </p>
                      <p className="text-sm text-neutral-text-muted mt-1">
                        Keep motivating your team to close deals
                      </p>
                    </div>
                  </div>
                  <div className="w-full md:w-auto">
                    <div className="bg-white rounded-xl p-4 border border-green-200">
                      <p className="text-xs text-green-600 font-medium mb-1">🎉 Goal Reached!</p>
                      <p className="text-3xl font-bold text-green-600">{recruits_count}<span className="text-lg text-green-400">/50</span></p>
                      <div className="w-full bg-green-100 rounded-full h-2 mt-2">
                        <div className="bg-green-500 h-2 rounded-full w-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            // In Progress Card
            <Card className="border-2 border-sky-200 bg-gradient-to-r from-sky-50 to-blue-50 shadow-card overflow-hidden" data-testid="target-card">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <Target className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-primary-900">🎯 Recruitment Target</h3>
                      <p className="text-primary-700 font-medium mt-1">
                        Recruit <span className="text-sky-600 font-bold">50 realtors</span> and get one to make a sale = <span className="text-green-600 font-bold">₦15,000 bonus!</span>
                      </p>
                      <p className="text-sm text-neutral-text-muted mt-1">
                        Plus your indirect commission and other benefits
                      </p>
                    </div>
                  </div>
                  <div className="w-full md:w-auto">
                    <div className="bg-white rounded-xl p-4 border border-sky-100">
                      <p className="text-xs text-neutral-text-muted mb-1">Your Progress</p>
                      <p className="text-3xl font-bold text-primary-900">{recruits_count || 0}<span className="text-lg text-neutral-400">/50</span></p>
                      <div className="w-full bg-neutral-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-sky-500 h-2 rounded-full transition-all" 
                          style={{ width: `${Math.min((recruits_count || 0) / MILESTONE_TARGET * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-neutral-border shadow-card" data-testid="stat-recruits">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-text-muted mb-1">Total Recruits</p>
                    <p className="text-3xl font-bold text-primary-900">{recruits_count || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-neutral-border shadow-card md:col-span-2" data-testid="referral-link-card">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-neutral-text-muted mb-3">Your Referral Link</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    readOnly
                    value={`${window.location.origin}${referral_link}`}
                    className="h-11 rounded-xl bg-neutral-50 font-mono text-sm flex-1"
                    data-testid="referral-link-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCopyLink}
                      className={`h-11 px-4 rounded-xl transition-all ${
                        copied
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-primary-900 hover:bg-primary-800'
                      }`}
                      data-testid="copy-link-btn"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </>
                      )}
                    </Button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`Join Medichi Tribe and start earning! Register here: ${window.location.origin}${referral_link}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        className="h-11 px-4 rounded-xl bg-green-500 hover:bg-green-600"
                        data-testid="share-whatsapp-btn"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                    </a>
                  </div>
                </div>
                <p className="text-xs text-neutral-400 mt-2">
                  Share this link with people you want to recruit. They'll be linked to you automatically!
                </p>
              </CardContent>
            </Card>
          </div>

          {/* WhatsApp Group CTA moved to top of dashboard */}

          {/* Training Library — every realtor sees the same list */}
          {trainings.length > 0 ? (
            <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 shadow-card" data-testid="training-library-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0"><PlayCircle className="h-6 w-6 text-white" /></div>
                  <div>
                    <CardTitle className="font-heading text-xl text-primary-900">Training Library</CardTitle>
                    <CardDescription>{trainings.length} training{trainings.length !== 1 ? 's' : ''} to help you succeed</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {trainings.map((tr) => {
                  const embed = getYouTubeEmbedUrl(tr.video_url);
                  return (
                    <div key={tr.id} className="bg-white rounded-xl p-4 border border-purple-100" data-testid={`training-${tr.id}`}>
                      <p className="font-semibold text-primary-900">{tr.title}</p>
                      {tr.description && <p className="text-sm text-neutral-text-muted mb-2">{tr.description}</p>}
                      {embed ? (
                        <div className="relative w-full pt-[56.25%] rounded-lg overflow-hidden bg-black">
                          <iframe className="absolute top-0 left-0 w-full h-full" src={embed} title={tr.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </div>
                      ) : (
                        <a href={tr.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-700 underline break-all">{tr.video_url}</a>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : (trainingVideoUrl && getYouTubeEmbedUrl(trainingVideoUrl) && (
            <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 shadow-card" data-testid="training-video-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0"><PlayCircle className="h-6 w-6 text-white" /></div>
                  <div>
                    <CardTitle className="font-heading text-xl text-primary-900">Training Video</CardTitle>
                    <CardDescription>Watch this video to learn how to maximize your success</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden bg-black">
                  <iframe className="absolute top-0 left-0 w-full h-full" src={getYouTubeEmbedUrl(trainingVideoUrl)} title="Training Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Profile Card */}
          <Card className="border-neutral-border shadow-card" data-testid="profile-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-primary-900">Your Profile</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Profile Photo Section */}
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-8 pb-6 border-b border-neutral-200">
                <div className="relative">
                  <input
                    type="file"
                    ref={profileImageInputRef}
                    onChange={handleProfileImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                  {profile?.profile_image_url ? (
                    <img 
                      src={profile.profile_image_url.startsWith('/api') ? `${API_BASE}${profile.profile_image_url}` : profile.profile_image_url}
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-primary-200"
                      onError={(e) => {
                        // Hide broken image and show fallback
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                  ) : null}
                  {/* Fallback avatar - shown when no image or image fails to load */}
                  <div 
                    className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center border-4 border-primary-200"
                    style={{ display: profile?.profile_image_url ? 'none' : 'flex' }}
                  >
                    <span className="text-4xl font-bold text-primary-600">
                      {profile?.full_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <button
                    onClick={() => profileImageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center shadow-lg transition-colors"
                  >
                    {uploadingImage ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-xl font-semibold text-primary-900">{profile?.full_name}</h3>
                  <p className="text-neutral-500">{profile?.email}</p>
                  <button
                    onClick={() => profileImageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <Upload className="h-3 w-3" />
                    {uploadingImage ? 'Uploading...' : profile?.profile_image_url ? 'Change Photo' : 'Upload Photo'}
                  </button>
                </div>
              </div>

              {/* Profile Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-muted">Full Name</p>
                    <p className="font-medium text-primary-900">{profile?.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-muted">Email</p>
                    <p className="font-medium text-primary-900">{profile?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-muted">Phone</p>
                    <p className="font-medium text-primary-900">{profile?.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-muted">Location</p>
                    <p className="font-medium text-primary-900">{profile?.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center">
                    <Link2 className="h-5 w-5 text-accent-600" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-text-muted">Referral Code</p>
                    <p className="font-mono font-bold text-accent-600">{profile?.referral_code}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recruits List */}
          <Card className="border-neutral-border shadow-card" data-testid="recruits-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-primary-900">Your Recruits</CardTitle>
              <CardDescription>People who joined using your referral link</CardDescription>
            </CardHeader>
            <CardContent>
              {recruits && recruits.length > 0 ? (
                <div className="space-y-4">
                  {recruits.map((recruit, index) => (
                    <div
                      key={recruit.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors"
                      data-testid={`recruit-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center">
                          <span className="font-semibold text-primary-800">
                            {recruit.full_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-primary-900">{recruit.full_name}</p>
                          <p className="text-sm text-neutral-text-muted">{recruit.location}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-primary-100 text-primary-700">
                        {new Date(recruit.created_at).toLocaleDateString()}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-neutral-400" />
                  </div>
                  <p className="text-neutral-text-muted mb-2">No recruits yet</p>
                  <p className="text-sm text-neutral-400">
                    Share your referral link to start building your network
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default MarketerDashboard;
